import { useEffect, useState, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Spin,
  Card,
  Empty,
  Alert,
  Tag,
  notification,
  Tooltip,
  Modal,
} from "antd";
import {
  ShoppingCartOutlined,
  EyeOutlined,
  UserOutlined,
  ShopOutlined,
  LeftOutlined,
  RightOutlined,
  TagOutlined,
  RiseOutlined,
  StarOutlined,
  SearchOutlined,
  MessageOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import "./styles.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MarketPlaceService from "../../utils/api/marketplace";
import OrderExpressService from "../../utils/api/orders-express";
import LayoutPage from "../../layouts/Main";
import MarketingServices from "../../utils/api/marketing";
import CategoriesData from "../../data/categories/categoriesData";
import { BASE_URL } from "../../utils/constants";
import FooterLogo from "../../images/epo_blanco.png";
import FacebookWhiteLogo from "../../images/LOGOS/facebook white.png";
import InstagramWhiteLogo from "../../images/LOGOS/Instagram white.png";
import LinkedinWhiteLogo from "../../images/LOGOS/linkedin white.png";
import TikTokWhiteLogo from "../../images/LOGOS/tik tok white.png";
import YoutubeWhiteLogo from "../../images/LOGOS/youtube white.png";

import useAppStore from "../../store/useAppStore";

export default function MarketplaceHomePage(props) {
  const history = useNavigate();
  const location = useLocation();
  const { category, subcategory } = useParams();
  const user = JSON.parse(localStorage.getItem("user"));
  const role = localStorage.getItem("role");
  const categoriesScrollRef = useRef(null);
  const updateCart = useAppStore((state) => state.updateCart);

  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentCategory, setCurrentCategory] = useState("");
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [addingToCart, setAddingToCart] = useState({});
  const [homeBanners, setHomeBanners] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const [featuredBanners, setFeaturedBanners] = useState([]);
  const [inlineBannerSizeMap, setInlineBannerSizeMap] = useState({});
  const [carouselCategories, setCarouselCategories] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pendingCartItem, setPendingCartItem] = useState(null);
  const [isOffersModalOpen, setIsOffersModalOpen] = useState(false);
  const [offersList, setOffersList] = useState([]);
  const [isTopModalOpen, setIsTopModalOpen] = useState(false);
  const [topList, setTopList] = useState([]);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [specialList, setSpecialList] = useState([]);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isQuoteRequestModalOpen, setIsQuoteRequestModalOpen] = useState(false);
  const [isSubmittingQuoteRequest, setIsSubmittingQuoteRequest] = useState(false);
  const [quoteRequestForm, setQuoteRequestForm] = useState({
    requesterName: "",
    email: "",
    company: "",
    department: "",
    productName: "",
    piecesCount: "",
    productDescription: "",
    productImageFile: null,
    supportDocumentFile: null,
  });

  const [visibleCount, setVisibleCount] = useState(24);
  const [currentSlide, setCurrentSlide] = useState(0);
  const autoPlayInterval = useRef(null);

  const OFFERS_STORAGE_KEY = "marketing_specials_offers";
  const resetAutoPlay = () => {
    if (autoPlayInterval.current) {
      clearInterval(autoPlayInterval.current);
      autoPlayInterval.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % homeBanners.length);
      }, 5000);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    resetAutoPlay();
  };

  const nextSlide = () => {
    const next = (currentSlide + 1) % homeBanners.length;
    goToSlide(next);
  };

  const prevSlide = () => {
    const prev = (currentSlide - 1 + homeBanners.length) % homeBanners.length;
    goToSlide(prev);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 992);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (homeBanners.length > 1) {
      autoPlayInterval.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % homeBanners.length);
      }, 5000);
      
      return () => {
        if (autoPlayInterval.current) {
          clearInterval(autoPlayInterval.current);
        }
      };
    }
  }, [homeBanners.length]);

  useEffect(() => {
    const inlineBanners = [...promoBanners, ...featuredBanners];

    if (inlineBanners.length === 0) {
      setInlineBannerSizeMap({});
      return;
    }

    let cancelled = false;

    const readImageSize = (banner) =>
      new Promise((resolve) => {
        if (!banner?.imageUrl) {
          resolve({ id: banner?.id, isExact: false });
          return;
        }

        const image = new window.Image();

        image.onload = () => {
          resolve({
            id: banner.id,
            isExact: image.naturalWidth === 656 && image.naturalHeight === 220,
          });
        };

        image.onerror = () => {
          resolve({ id: banner.id, isExact: false });
        };

        image.src = banner.imageUrl;
      });

    Promise.all(inlineBanners.map(readImageSize)).then((results) => {
      if (cancelled) return;
      const nextMap = {};
      results.forEach((result) => {
        if (result?.id !== undefined && result?.id !== null) {
          nextMap[result.id] = result.isExact;
        }
      });
      setInlineBannerSizeMap(nextMap);
    });

    return () => {
      cancelled = true;
    };
  }, [promoBanners, featuredBanners]);

  const loadMarketingBanners = useCallback(async () => {
    try {
      const resp = await MarketingServices.GetAll();
      
      const sortedBanners = (resp.data?.banners || []).sort(
        (a, b) => a.order - b.order
      );
      
      
      const home = sortedBanners.filter((banner) => banner.section === "home");
      const promos = sortedBanners.filter((banner) => banner.section === "promos");
      const featured = sortedBanners.filter((banner) => banner.section === "featured");
      
      
      setHomeBanners(home);
      setPromoBanners(promos);
      setFeaturedBanners(featured);
      
      setCurrentSlide(0);
      
    } catch (error) {
      setHomeBanners([]);
      setPromoBanners([]);
      setFeaturedBanners([]);
    }
  }, []);

  const transformCategories = useCallback((categoriesData) => {
    const validCategories = categoriesData.filter(
      (cat) => cat && cat.name && cat.slug
    );

    const categoriesMap = {};
    validCategories.forEach((categoryItem) => {
      categoriesMap[categoryItem.slug] = {
        ...categoryItem,
        subcategories: [],
      };
    });

    const rootCategories = [];
    validCategories.forEach((categoryItem) => {
      if (categoryItem.parent_slug) {
        const parent = categoriesMap[categoryItem.parent_slug];
        if (parent) {
          parent.subcategories.push(categoriesMap[categoryItem.slug]);
        } else {
          rootCategories.push(categoriesMap[categoryItem.slug]);
        }
      } else {
        rootCategories.push(categoriesMap[categoryItem.slug]);
      }
    });

    return rootCategories;
  }, []);

  const getCategoryCardImage = (categoryItem) => {
    const remoteImage =
      categoryItem?.image ||
      categoryItem?.imageBase64 ||
      categoryItem?.image_base64 ||
      categoryItem?.image_url ||
      categoryItem?.imageUrl ||
      categoryItem?.carousel_image_url ||
      categoryItem?.carouselImageUrl;
    if (remoteImage) {
      const normalized = String(remoteImage).trim();
      if (normalized.startsWith("data:") || normalized.startsWith("http")) {
        return normalized;
      }
      if (normalized.startsWith("/")) {
        return normalized;
      }
      const isLikelyBase64 =
        /^[A-Za-z0-9+/]+={0,2}$/.test(normalized) && normalized.length > 100;
      if (isLikelyBase64) {
        return `data:image/jpeg;base64,${normalized}`;
      }
      return `${BASE_URL.replace(/\/$/, "")}/${normalized.replace(/^\/+/, "")}`;
    }
    const slug = (categoryItem?.slug || "").toLowerCase();
    const name = (categoryItem?.name || "").toLowerCase();
    const match = CategoriesData.find((item) => {
      const itemSlug = (item.slug || "").toLowerCase();
      const itemName = (item.category || item.name || "").toLowerCase();
      return (slug && itemSlug === slug) || (name && itemName === name);
    });
    return match?.img || "/img/epno.png";
  };

  const loadCarouselCategories = useCallback(async () => {
    try {
      const response = await MarketPlaceService.getCategories();

      let categoriesData = [];
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        categoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (response.success && Array.isArray(response.categories)) {
        categoriesData = response.categories;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      const transformedCategories = transformCategories(categoriesData);
      setCarouselCategories(transformedCategories);
    } catch (error) {
      setCarouselCategories([]);
    }
  }, [transformCategories]);

  const loadProducts = useCallback(async () => {
    setError(null);

    try {
      let response;
      if (subcategory) {
        response = await MarketPlaceService.getAllProducts();
      } else if (category) {
        response = await MarketPlaceService.getProductsByCategory(category);
      } else {
        response = await MarketPlaceService.getAllProducts();
      }

      let productsArray = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.products)
      ) {
        productsArray = response.data.products;
      } else if (response.data && Array.isArray(response.data)) {
        productsArray = response.data;
      } else if (Array.isArray(response)) {
        productsArray = response;
      }
      const validProducts = productsArray.filter(
        (product) => product && product.name && !product.archive
      );

      let finalProducts = validProducts;

      if (subcategory) {
        finalProducts = validProducts.filter(
          (product) =>
            product.sub_category &&
            Array.isArray(product.sub_category) &&
            product.sub_category.some(
              (sub) => sub.toLowerCase() === subcategory.toLowerCase()
            )
        );

        setCurrentCategory(
          `${formatCategoryName(category)} / ${formatCategoryName(subcategory)}`
        );
      } else if (category) {
        finalProducts = validProducts.filter(
          (product) =>
            product.category &&
            product.category.toLowerCase() === category.toLowerCase()
        );
        setCurrentCategory(formatCategoryName(category));
      } else {
        setCurrentCategory("Todos los productos");
      }

      setAllProducts(finalProducts);
      setFilteredProducts(finalProducts);
    } catch (error) {
      setError("Error al cargar los productos");
      setAllProducts([]);
      setFilteredProducts([]);
    }
  }, [category, subcategory]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await loadMarketingBanners();
      await loadCarouselCategories();
      await loadProducts();
    } catch (error) {
      setError("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, [loadMarketingBanners, loadProducts, loadCarouselCategories]);

  const formatCategoryName = (name) => {
    if (!name) return "";
    return name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const rawLoginFlag = (
      query.get("login") ||
      query.get("requireLogin") ||
      ""
    ).toLowerCase();
    const shouldRequestLogin =
      rawLoginFlag === "1" || rawLoginFlag === "true" || rawLoginFlag === "yes";

    if (!shouldRequestLogin) return;

    let parsedUser = null;
    try {
      parsedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
      parsedUser = null;
    }

    const hasSession = Boolean(localStorage.getItem("token") && parsedUser?.id);
    if (hasSession) return;

    const explicitRedirect = query.get("redirect") || query.get("redirectTo");
    const cleanedQuery = new URLSearchParams(location.search);
    ["login", "requireLogin", "redirect", "redirectTo"].forEach((param) =>
      cleanedQuery.delete(param)
    );
    const cleanSearch = cleanedQuery.toString();
    const currentPagePath = `${location.pathname}${
      cleanSearch ? `?${cleanSearch}` : ""
    }${location.hash || ""}`;

    localStorage.setItem(
      "post_login_redirect",
      explicitRedirect || currentPagePath
    );
    setIsLoginModalOpen(true);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    setVisibleCount(24);
  }, [category, subcategory, searchTerm]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    let searchFromQuery = (query.get("search") || "").trim();

    if (!searchFromQuery && location.hash) {
      const hashQueryIndex = location.hash.indexOf("?");
      if (hashQueryIndex !== -1) {
        const hashQuery = location.hash.slice(hashQueryIndex + 1);
        searchFromQuery = (
          new URLSearchParams(hashQuery).get("search") || ""
        ).trim();
      }
    }

    setSearchTerm((prev) =>
      prev === searchFromQuery ? prev : searchFromQuery
    );
  }, [location.search, location.hash]);

  useEffect(() => {
    const handleSearchEvent = (event) => {
      setSearchTerm(event.detail);
    };

    window.addEventListener("marketplace_search", handleSearchEvent);

    return () => {
      window.removeEventListener("marketplace_search", handleSearchEvent);
    };
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(allProducts);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = allProducts.filter(
        (product) =>
          (product.name || "").toLowerCase().includes(searchTermLower) ||
          (product.description || "").toLowerCase().includes(searchTermLower) ||
          (product.category || "").toLowerCase().includes(searchTermLower) ||
          (product.sku || "").toLowerCase().includes(searchTermLower)
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, allProducts]);

  const getCurrentPageProducts = () => {
    return filteredProducts.slice(0, visibleCount);
  };

  const loadMoreProducts = () => {
    setVisibleCount((prev) => prev + 24);
  };

  const hasMoreProducts = visibleCount < filteredProducts.length;

  const formatPrice = (price) => {
    if (typeof price === "number") {
      return `$${price.toFixed(2)}`;
    }
    if (typeof price === "string") {
      const numPrice = parseFloat(price);
      return isNaN(numPrice) ? "Consultar precio" : `$${numPrice.toFixed(2)}`;
    }
    return "Consultar precio";
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Sin vigencia";
    const formatDate = (value) => {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      return parsed.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };
    return `${formatDate(start)} - ${formatDate(end)}`.trim();
  };

  const getProductName = (product) => {
    return product.name || "Producto sin nombre";
  };

  const loadStoredOffers = () => {
    try {
      const stored = localStorage.getItem(OFFERS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      return [];
    }
    return [];
  };

  const handleOpenOffers = () => {
    const storedOffers = loadStoredOffers();
    const offers = storedOffers
      .filter((offer) => offer.tag === "Ofertas")
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    setOffersList(offers);
    setIsOffersModalOpen(true);
  };

  const handleCloseOffers = () => {
    setIsOffersModalOpen(false);
  };

  const handleOpenTop = () => {
    const storedOffers = loadStoredOffers();
    const tops = storedOffers
      .filter((offer) => offer.tag === "Productos Top")
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 3);
    setTopList(tops);
    setIsTopModalOpen(true);
  };

  const handleCloseTop = () => {
    setIsTopModalOpen(false);
  };

  const handleOpenSpecial = () => {
    const storedOffers = loadStoredOffers();
    const specials = storedOffers
      .filter((offer) => offer.tag === "Especiales")
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    setSpecialList(specials);
    setIsSpecialModalOpen(true);
  };

  const handleCloseSpecial = () => {
    setIsSpecialModalOpen(false);
  };

  const handleOpenTerms = () => {
    setIsTermsModalOpen(true);
  };

  const handleCloseTerms = () => {
    setIsTermsModalOpen(false);
  };

  const handleOpenPrivacy = () => {
    setIsPrivacyModalOpen(true);
  };

  const handleClosePrivacy = () => {
    setIsPrivacyModalOpen(false);
  };

  const handleOpenQuoteRequest = () => {
    setIsQuoteRequestModalOpen(true);
  };

  const handleCloseQuoteRequest = () => {
    setIsQuoteRequestModalOpen(false);
  };

  const handleQuoteRequestInputChange = (event) => {
    const { name, value } = event.target;
    setQuoteRequestForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuoteRequestFileChange = (event, fieldName) => {
    const selectedFile =
      event.target.files && event.target.files[0]
        ? event.target.files[0]
        : null;
    setQuoteRequestForm((prev) => ({
      ...prev,
      [fieldName]: selectedFile,
    }));
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = (error) => reject(error);
    });

  const resetQuoteRequestForm = () => {
    setQuoteRequestForm({
      requesterName: "",
      email: "",
      company: "",
      department: "",
      productName: "",
      piecesCount: "",
      productDescription: "",
      productImageFile: null,
      supportDocumentFile: null,
    });
  };

  const handleQuoteRequestSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      quoteRequestForm.requesterName,
      quoteRequestForm.email,
      quoteRequestForm.productName,
      quoteRequestForm.piecesCount,
    ];

    if (requiredFields.some((field) => !String(field || "").trim())) {
      notification.warning({
        message: "Campos obligatorios",
        description:
          "Completa los campos obligatorios para enviar la cotización.",
        placement: "top",
        duration: 4,
      });
      return;
    }

    setIsSubmittingQuoteRequest(true);

    try {
      const requesterName = quoteRequestForm.requesterName.trim();
      const email = quoteRequestForm.email.trim();
      const company = quoteRequestForm.company.trim();
      const department = quoteRequestForm.department.trim();
      const productName = quoteRequestForm.productName.trim();
      const quantity = Number(quoteRequestForm.piecesCount) || 1;
      const description = quoteRequestForm.productDescription.trim();

      const selectedFiles = [];
      if (quoteRequestForm.productImageFile) {
        selectedFiles.push(quoteRequestForm.productImageFile);
      }
      if (quoteRequestForm.supportDocumentFile) {
        selectedFiles.push(quoteRequestForm.supportDocumentFile);
      }

      const file1 = selectedFiles[0] ? await getBase64(selectedFiles[0]) : "";
      const file2 = selectedFiles[1] ? await getBase64(selectedFiles[1]) : "";

      const payload = {
        name: requesterName,
        email,
        bussiness: company,
        department,
        product_name: productName,
        quantity,
        description,
        file1,
        file2,
      };

      const response = await OrderExpressService.Create(payload);
      const createdOrder =
        response?.data?.order_id || response?.data?.orders?.[0]?.id;

      const localAttachments = [file1, file2].filter(Boolean);

      if (createdOrder && localAttachments.length > 0) {
        const sessionMap = window.__ordersExpressAttachments || {};
        sessionMap[createdOrder] = localAttachments;
        window.__ordersExpressAttachments = sessionMap;

        try {
          const storageKey = "orders_express_local_attachments";
          const rawStored = localStorage.getItem(storageKey);
          const parsedStored = rawStored ? JSON.parse(rawStored) : {};
          parsedStored[createdOrder] = localAttachments;
          localStorage.setItem(storageKey, JSON.stringify(parsedStored));
        } catch (error) {
          // localStorage puede fallar por tamaño; usamos respaldo en memoria de sesión
        }
      }

      notification.success({
        message: "Solicitud enviada",
        description: createdOrder
          ? `Tu solicitud fue registrada con folio ${createdOrder}.`
          : "Tu solicitud de cotización fue enviada correctamente.",
        placement: "top",
        duration: 4,
      });

      resetQuoteRequestForm();
      setIsQuoteRequestModalOpen(false);
    } catch (error) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        "No se pudo enviar la solicitud de cotización.";
      notification.error({
        message: "Error al enviar",
        description: errorMessage,
        placement: "top",
        duration: 4,
      });
    } finally {
      setIsSubmittingQuoteRequest(false);
    }
  };

  const addToCart = async (product) => {
    if (!product.stock || product.stock <= 0) {
      notification.warning({
        message: "Producto sin stock",
        description: "Este producto no tiene inventario disponible actualmente.",
        placement: "top",
        duration: 3,
      });
      return;
    }
  
    if (!user) {
      const finalPrice =
        product.newPrice && product.newPrice !== product.price
          ? Number(product.newPrice)
          : Number(product.price) || 0;
  
      setPendingCartItem({
        id: product.id,
        product_id: product.id,
        supplier_id: product.supplier_id || "default-supplier",
        name: product.name,
        category: product.category,
        sub_category: product.sub_category || product.subcategory || "general",
        quantity: 1,
        price: finalPrice,
        imgs: product.images || [],
        description: product.description || "",
      });
      setIsLoginModalOpen(true);
      return;
    }
  
    if (addingToCart[product.id]) {
      return;
    }
  
    setAddingToCart((prev) => ({ ...prev, [product.id]: true }));
  
    try {
      let cartResponse;
      try {
        cartResponse = await MarketPlaceService.getCartByClient(user.id);
      } catch (error) {
        cartResponse = { data: null };
      }
  
      let currentCart = cartResponse.data;
      let productsInCart = [];
  
      const finalPrice =
        product.newPrice && product.newPrice !== product.price
          ? Number(product.newPrice)
          : Number(product.price) || 0;
  
      if (
        currentCart &&
        currentCart.products &&
        Array.isArray(currentCart.products)
      ) {
        productsInCart = currentCart.products.filter(
          (item) => item.user_id === user.id && item.user_role === user.role
        );
  
        const existingProductIndex = productsInCart.findIndex(
          (item) => item.id === product.id || item.product_id === product.id
        );
  
        if (existingProductIndex > -1) {
          const currentQuantity = productsInCart[existingProductIndex].quantity;
          const newQuantity = currentQuantity + 1;
          
          if (newQuantity > product.stock) {
            notification.warning({
              message: "Stock insuficiente",
              description: `Solo hay ${product.stock} unidad(es) disponibles de este producto.`,
              placement: "top",
              duration: 3,
            });
            setAddingToCart((prev) => ({ ...prev, [product.id]: false }));
            return;
          }
          
          productsInCart[existingProductIndex].quantity = newQuantity;
          productsInCart[existingProductIndex].price = finalPrice;
        } else {
          if (1 > product.stock) {
            notification.warning({
              message: "Stock insuficiente",
              description: `No hay suficiente stock disponible de este producto.`,
              placement: "topRight",
              duration: 3,
            });
            setAddingToCart((prev) => ({ ...prev, [product.id]: false }));
            return;
          }
          
          productsInCart.push({
            id: product.id,
            product_id: product.id,
            supplier_id: product.supplier_id || "default-supplier",
            name: product.name,
            category: product.category,
            sub_category: product.sub_category || product.subcategory || "general",
            quantity: 1,
            price: finalPrice,
            imgs: product.images || [],
            description: product.description || "",
            user_role: user.role,
            user_id: user.id,
          });
        }
      } else {
        if (1 > product.stock) {
          notification.warning({
            message: "Stock insuficiente",
            description: `No hay suficiente stock disponible de este producto.`,
            placement: "top",
            duration: 3,
          });
          setAddingToCart((prev) => ({ ...prev, [product.id]: false }));
          return;
        }
        
        productsInCart = [
          {
            id: product.id,
            product_id: product.id,
            supplier_id: product.supplier_id || "default-supplier",
            name: product.name,
            category: product.category,
            sub_category: product.sub_category || product.subcategory || "general",
            quantity: 1,
            price: finalPrice,
            imgs: product.images || [],
            description: product.description || "",
            user_role: user.role,
            user_id: user.id,
          },
        ];
      }
  
      const totalPrice = productsInCart.reduce(
        (sum, item) => sum + Number(item.price) * Number(item.quantity),
        0
      );
  
      const cartData = {
        client_id: user.id,
        client_role: user.role,
        products: productsInCart,
        price: totalPrice,
        change_type: "MX",
        status: 1,
      };
  
      let result;
      if (currentCart && currentCart.id) {
        result = await MarketPlaceService.updateCart(cartData, currentCart.id);
      } else {
        result = await MarketPlaceService.addProductToCart(cartData);
      }
  
      const cartKey = `cart_${user.id}_${user.role}`;
      localStorage.setItem(cartKey, JSON.stringify(productsInCart));
  
      if (user) {
        const updatedCart = {
          ...currentCart,
          products: productsInCart,
        };
        updateCart(updatedCart);
      }
  
      window.dispatchEvent(new Event("cartUpdated"));
  
      notification.success({
        description: `${product.name} se ha añadido al carrito correctamente`,
        placement: "top",
        duration: 3,
      });
    } catch (error) {
      let errorMessage = "No se pudo agregar el producto al carrito";
  
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = "Error de conexión. Verifica tu internet.";
      }
  
      notification.error({
        description: errorMessage,
        placement: "top",
        duration: 4,
      });
    } finally {
      setAddingToCart((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const savePendingCartItem = () => {
    if (!pendingCartItem) return;
    const pendingKey = "pending_cart_items";
    let pendingItems = [];

    try {
      const existing = localStorage.getItem(pendingKey);
      if (existing) {
        pendingItems = JSON.parse(existing);
      }
    } catch (error) {
      pendingItems = [];
    }

    const existingIndex = pendingItems.findIndex(
      (item) =>
        item.id === pendingCartItem.id ||
        item.product_id === pendingCartItem.product_id
    );

    if (existingIndex > -1) {
      pendingItems[existingIndex].quantity += Number(
        pendingCartItem.quantity || 1
      );
      pendingItems[existingIndex].price =
        Number(pendingCartItem.price) || pendingItems[existingIndex].price || 0;
    } else {
      pendingItems.push(pendingCartItem);
    }

    localStorage.setItem(pendingKey, JSON.stringify(pendingItems));
    localStorage.setItem("post_login_redirect", "/marketplace/shopping-cart");
  };

  const handleLoginRedirect = () => {
    savePendingCartItem();
    history("/login");
  };

  const handleRegisterRedirect = () => {
    savePendingCartItem();
    history("/register");
  };

  const viewProductDetails = (product, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (product.id) {
      history(`/marketplace/product/${product.id}`);
    } else {
      notification.warning({
        message: "Información",
        description: "Detalles del producto no disponibles",
      });
    }
  };

  const clearFilters = () => {
    history("/marketplace");
    setSearchTerm("");
  };

  const showAllFilteredProducts = () => {
    setVisibleCount(filteredProducts.length || 24);
  };

  const handleImageLoad = (productId) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  const handleImageError = (productId) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [productId]: false,
    }));
  };

  const getOptimizedImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    return imageUrl;
  };

  const getBannerLink = (banner) => {
    return banner?.url || "";
  };

  const handleBannerClick = (banner) => {
    const rawLink = getBannerLink(banner);
    if (!rawLink) return;

    const normalizedLink = String(rawLink).trim();
    if (!normalizedLink) return;

    if (/^https?:\/\//i.test(normalizedLink)) {
      window.open(normalizedLink, "_blank", "noopener,noreferrer");
      return;
    }

    const internalPath = normalizedLink.startsWith("/")
      ? normalizedLink
      : `/${normalizedLink}`;
    history(internalPath);
  };

  const renderMainBanner = () => {
  
    if (homeBanners.length === 0) {
      return (
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            height: "100%",
          }}
          className="banner-slide"
        >
          <div className="banner-text">
            <h1>Bienvenido al Marketplace</h1>
            <p>Descubre los mejores productos y servicios</p>
          </div>
        </div>
      );
    }

    return (
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          overflow: 'hidden'
        }}>
          {homeBanners.map((banner, index) => (
            <div
              key={banner.id}
              onClick={() => handleBannerClick(banner)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: index === currentSlide ? 1 : 0,
                transition: 'opacity 0.8s ease-in-out',
                cursor: getBannerLink(banner) ? "pointer" : "default",
                zIndex: index === currentSlide ? 2 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
              }}
            >
              <img
                src={banner.imageUrl}
                alt={banner.title || "Banner"}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
                onLoad={() => {
                  console.log(`Imagen del banner ${index + 1} cargada:`, banner.title);
                }}
                onError={(e) => {
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pjwvc3ZnPg==";
                }}
              />
            </div>
          ))}
        </div>

        {homeBanners.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: '15px',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            zIndex: 10
          }}>
            {homeBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                style={{
                  width: index === currentSlide ? '30px' : '10px',
                  height: '10px',
                  borderRadius: '5px',
                  border: 'none',
                  backgroundColor: index === currentSlide ? '#1890ff' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  padding: 0,
                  margin: 0
                }}
                aria-label={`Ir al slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {homeBanners.length > 1 && (
          <div
            onClick={prevSlide}
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '40px',
              height: '40px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
          >
            <LeftOutlined style={{ fontSize: '20px' }} />
          </div>
        )}

        {homeBanners.length > 1 && (
          <div
            onClick={nextSlide}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '40px',
              height: '40px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
          >
            <RightOutlined style={{ fontSize: '20px' }} />
          </div>
        )}

        {process.env.NODE_ENV === "development" && (
          <div style={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "5px 10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 1000,
            pointerEvents: "none"
          }}>
            Banners: {homeBanners.length} | Slide: {currentSlide + 1}/{homeBanners.length}
          </div>
        )}
      </div>
    );
  };

  const renderInlineBannerSection = (title, subtitle, banners, variant) => {
    if (!banners || banners.length === 0) return null;

    return (
      <div className={`marketing-inline-section marketing-inline-${variant}`}>
        <div className="marketing-inline-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
        </div>
        <div className="marketing-inline-banner">
          {banners.map((banner) => (
            <div
              key={banner.id}
              className="banner-slide marketing-inline-slide"
              onClick={() => handleBannerClick(banner)}
              style={{
                cursor: getBannerLink(banner) ? "pointer" : "default",
              }}
            >
              <img
                src={banner.imageUrl}
                alt={banner.title || title}
                className="marketing-inline-image"
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductCard = (product, index) => (
    <Col
      xs={12}
      sm={8}
      md={6}
      lg={6}
      xl={4}
      key={product.id || `product-${index}`}
    >
      <Card
        hoverable
        className="product-card-responsive"
        cover={
          <div
            className="product-image-container-responsive"
            onClick={() => viewProductDetails(product)}
            style={{ cursor: "pointer" }}
          >
            {product.images && product.images.length > 0 ? (
              <>
                {!imageLoadingStates[product.id] && (
                  <div className="image-loading-placeholder">
                    <Spin size="small" />
                  </div>
                )}
                <img
                  alt={product.name}
                  src={getOptimizedImageUrl(product.images[0])}
                  className={`product-image-responsive ${
                    imageLoadingStates[product.id] ? "loaded" : "loading"
                  }`}
                  onLoad={() => handleImageLoad(product.id)}
                  onError={() => handleImageError(product.id)}
                  loading="lazy"
                />
              </>
            ) : (
              <div className="product-image-placeholder-responsive">
                <ShoppingCartOutlined />
              </div>
            )}
            <div className="product-overlay-responsive">
              <Tooltip title="Ver detalles">
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={(e) => viewProductDetails(product, e)}
                  className="overlay-button"
                  size={isMobile || isTablet ? "small" : "middle"}
                />
              </Tooltip>
            </div>
          </div>
        }
      >
        <div
          className="product-info-responsive"
          onClick={() => viewProductDetails(product)}
          style={{ cursor: "pointer" }}
        >
          <div className="product-name-responsive">
            {getProductName(product)}
          </div>

          <div className="product-price-section-responsive">
            {renderPriceSection(product)}
          </div>
        </div>

        <div className="product-footer-responsive">
          <div className="stock-and-cart-container">
            <div className="stock-status" style={{ display: "none" }}>
              {product.stock > 0 ? `Stock: ${product.stock}` : "Sin stock"}
            </div>

            {renderAddToCartButton(product)}
          </div>
        </div>
      </Card>
    </Col>
  );

  const renderAddToCartButton = (product) => {
    const buttonText = addingToCart[product.id] ? "Agregando..." : "Agregar";
    const isOutOfStock = !product.stock || product.stock <= 0;
    const isLowStock = product.stock > 0 && product.stock <= 5;
  
    return (
      <Tooltip title={isOutOfStock ? "Sin stock disponible" : "Agregar al carrito"}>
        <Button
          type="primary"
          icon={
            addingToCart[product.id] ? (
              <Spin size="small" />
            ) : (
              <ShoppingCartOutlined />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            addToCart(product);
          }}
          disabled={addingToCart[product.id] || isOutOfStock}
          className={`add-to-cart-button-enhanced ${
            isOutOfStock ? "out-of-stock-button" : ""
          } ${isLowStock && !isOutOfStock ? "low-stock-button" : ""}`}
          loading={addingToCart[product.id]}
          size="middle"
        >
          {isOutOfStock ? "Sin stock" : buttonText}
        </Button>
      </Tooltip>
    );
  };

  const renderLoginModal = () => (
    <Modal
      open={isLoginModalOpen}
      onCancel={() => setIsLoginModalOpen(false)}
      footer={null}
      centered
      closable
      width={420}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          background: "#0F5197",
          color: "#fff",
          padding: "28px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.15)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <ShoppingCartOutlined style={{ fontSize: 28, color: "#fff" }} />
        </div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          ¡Inicia sesión para continuar!
        </h2>
        <p style={{ margin: "8px 0 0", fontSize: 14, opacity: 0.95 }}>
          Crea una cuenta o inicia sesión para agregar productos a tu carrito
        </p>
      </div>
      <div style={{ padding: "18px 24px 24px" }}>
        <Button
          type="primary"
          block
          size="large"
          onClick={handleRegisterRedirect}
          style={{
            background: "#0F5197",
            borderColor: "#0F5197",
            fontWeight: 600,
          }}
        >
          Crear cuenta
        </Button>
        <Button
          block
          size="large"
          onClick={handleLoginRedirect}
          style={{ marginTop: 12, fontWeight: 600 }}
        >
          Ya tengo cuenta
        </Button>
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            textAlign: "center",
            color: "#8a8a8a",
          }}
        >
          Al continuar, aceptas nuestros términos de servicio y política de
          privacidad
        </div>
      </div>
    </Modal>
  );

  const renderOffersModal = () => (
    <Modal
      open={isOffersModalOpen}
      onCancel={handleCloseOffers}
      footer={null}
      centered
      width={920}
      className="offers-modal"
    >
      <div className="offers-modal-header">
        <div className="offers-modal-title">OFERTAS ESPECIALES</div>
        <div className="offers-modal-subtitle">
          Descuentos increíbles por tiempo limitado!
        </div>
      </div>
      <div className="offers-modal-body">
        {offersList.length === 0 ? (
          <Empty description="No hay ofertas disponibles" />
        ) : (
          <div className="offers-modal-list">
            {offersList.map((offer) => (
              <div key={offer.id} className="offers-modal-item">
                <div className="offers-modal-image">
                  <img src={offer.imageUrl} alt={offer.title} />
                  <span className="offers-modal-badge">
                    -{offer.discountPct}%
                  </span>
                </div>
                <div className="offers-modal-info">
                  <div className="offers-modal-name">{offer.title}</div>
                  <div className="offers-modal-desc">{offer.description}</div>
                </div>
                <div className="offers-modal-prices">
                  <div className="offers-modal-price">
                    {formatPrice(offer.price)}
                  </div>
                  <div className="offers-modal-old">
                    {formatPrice(offer.originalPrice)}
                  </div>
                  <div className="offers-modal-save">
                    Ahorra{" "}
                    {formatPrice(
                      (offer.originalPrice || 0) - (offer.price || 0)
                    )}
                  </div>
                </div>
                <div className="offers-modal-action">
                  <Button type="primary" className="offers-modal-add">
                    Agregar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );

  const renderTopModal = () => (
    <Modal
      open={isTopModalOpen}
      onCancel={handleCloseTop}
      footer={null}
      centered
      width={980}
      className="top-modal"
    >
      <div className="top-modal-header">
        <div className="top-modal-title">TOP 3 PRODUCTOS MÁS POPULARES</div>
        <div className="top-modal-subtitle">
          Los favoritos de nuestros clientes
        </div>
      </div>
      <div className="top-modal-body">
        {topList.length === 0 ? (
          <Empty description="No hay productos top disponibles" />
        ) : (
          <div className="top-modal-grid">
            {topList.map((offer, index) => (
              <div key={offer.id} className="top-modal-card">
                <div className={`top-modal-rank top-modal-rank--${index + 1}`}>
                  #{index + 1}
                </div>
                <div className="top-modal-image">
                  <img src={offer.imageUrl} alt={offer.title} />
                </div>
                <div className="top-modal-name">{offer.title}</div>
                <div className="top-modal-desc">{offer.description}</div>
                <div className="top-modal-price">
                  {formatPrice(offer.price)}
                </div>
                <Button type="primary" className="top-modal-buy">
                  Comprar
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );

  const renderSpecialModal = () => (
    <Modal
      open={isSpecialModalOpen}
      onCancel={handleCloseSpecial}
      footer={null}
      centered
      width={940}
      className="special-modal"
    >
      <div className="special-modal-header">
        <div className="special-modal-title">PRODUCTOS ESPECIALES</div>
        <div className="special-modal-subtitle">
          Ofertas exclusivas por tiempo limitado
        </div>
      </div>
      <div className="special-modal-body">
        {specialList.length === 0 ? (
          <Empty description="No hay productos especiales disponibles" />
        ) : (
          <div className="special-modal-grid">
            {specialList.map((offer) => (
              <div key={offer.id} className="special-modal-card">
                <div className="special-modal-badge">EXCLUSIVO</div>
                <div className="special-modal-image">
                  <img src={offer.imageUrl} alt={offer.title} />
                </div>
                <div className="special-modal-discount">
                  -{offer.discountPct}%
                </div>
                <div className="special-modal-name">{offer.title}</div>
                <div className="special-modal-desc">{offer.description}</div>
                <div className="special-modal-prices">
                  <div className="special-modal-price">
                    {formatPrice(offer.price)}
                  </div>
                  <div className="special-modal-old">
                    {formatPrice(offer.originalPrice)}
                  </div>
                  <div className="special-modal-save">
                    Ahorra{" "}
                    {formatPrice(
                      (Number(offer.originalPrice) || 0) -
                        (Number(offer.price) || 0)
                    )}
                  </div>
                </div>
                <div className="special-modal-details">
                  <span>
                    {offer.category
                      ? `Categoria: ${offer.category}`
                      : "Categoría no disponible"}
                  </span>
                  <span>
                    Vigencia: {formatDateRange(offer.start, offer.end)}
                  </span>
                  <span>
                    {offer.tag
                      ? `Etiqueta: ${offer.tag}`
                      : "Etiqueta no disponible"}
                  </span>
                  <span>
                    {offer.discountPct
                      ? `Descuento: ${offer.discountPct}%`
                      : "Descuento no disponible"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );

  const renderQuoteRequestModal = () => (
    <Modal
      open={isQuoteRequestModalOpen}
      onCancel={handleCloseQuoteRequest}
      footer={null}
      centered
      width={1100}
      className="quote-request-modal"
      destroyOnClose={false}
    >
      <div className="quote-request-modal-body">
        <h2>Cotización express</h2>
        <p>
          Envía tus requerimientos ahora y recibe una propuesta rápida y clara.
        </p>
        <form
          className="quote-request-form"
          onSubmit={handleQuoteRequestSubmit}
        >
          <div className="quote-request-grid quote-request-grid--three">
            <label className="quote-request-field">
              <span>
                Nombre del requisitor <em>*</em>
              </span>
              <input
                type="text"
                name="requesterName"
                value={quoteRequestForm.requesterName}
                onChange={handleQuoteRequestInputChange}
                placeholder="Nombre del requisitor"
                required
              />
            </label>

            <label className="quote-request-field">
              <span>
                Correo <em>*</em>
              </span>
              <input
                type="email"
                name="email"
                value={quoteRequestForm.email}
                onChange={handleQuoteRequestInputChange}
                placeholder="Correo"
                required
              />
            </label>

            <label className="quote-request-field">
              <span>Empresa</span>
              <input
                type="text"
                name="company"
                value={quoteRequestForm.company}
                onChange={handleQuoteRequestInputChange}
                placeholder="Empresa"
              />
            </label>

            <label className="quote-request-field">
              <span>Departamento</span>
              <input
                type="text"
                name="department"
                value={quoteRequestForm.department}
                onChange={handleQuoteRequestInputChange}
                placeholder="Departamento"
              />
            </label>

            <label className="quote-request-field">
              <span>
                Nombre del producto <em>*</em>
              </span>
              <input
                type="text"
                name="productName"
                value={quoteRequestForm.productName}
                onChange={handleQuoteRequestInputChange}
                placeholder="Nombre del producto"
                required
              />
            </label>

            <label className="quote-request-field">
              <span>
                Número de piezas <em>*</em>
              </span>
              <input
                type="number"
                name="piecesCount"
                value={quoteRequestForm.piecesCount}
                onChange={handleQuoteRequestInputChange}
                placeholder="Número de piezas"
                min="1"
                required
              />
            </label>
          </div>

          <label className="quote-request-field quote-request-field--full">
            <span>Descripción del producto</span>
            <textarea
              name="productDescription"
              value={quoteRequestForm.productDescription}
              onChange={handleQuoteRequestInputChange}
              placeholder="Descripción del producto"
            />
          </label>

          <div className="quote-request-grid quote-request-grid--actions">
            <label className="quote-request-upload-field">
              <span>Imagen del producto (formato jpg. png.)</span>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(event) =>
                  handleQuoteRequestFileChange(event, "productImageFile")
                }
              />
              <div className="quote-request-upload-trigger">
                <PaperClipOutlined />
                <strong>
                  {quoteRequestForm.productImageFile
                    ? quoteRequestForm.productImageFile.name
                    : "Seleccionar imagen"}
                </strong>
              </div>
            </label>

            <label className="quote-request-upload-field">
              <span>Otros documentos de apoyo (formato pdf. txt. exc. )</span>
              <input
                type="file"
                accept=".pdf,.txt,.xlsx,.xls,.doc,.docx"
                onChange={(event) =>
                  handleQuoteRequestFileChange(event, "supportDocumentFile")
                }
              />
              <div className="quote-request-upload-trigger">
                <PaperClipOutlined />
                <strong>
                  {quoteRequestForm.supportDocumentFile
                    ? quoteRequestForm.supportDocumentFile.name
                    : "Seleccionar documento"}
                </strong>
              </div>
            </label>

            <div className="quote-request-submit-wrapper">
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmittingQuoteRequest}
                className="quote-request-submit-button"
              >
                COTIZAR AHORA
              </Button>
            </div>
          </div>

          <div className="quote-request-required">* Campos obligatorios</div>
        </form>
      </div>
    </Modal>
  );

  const renderPriceSection = (product) => {
    if (role === "1") {
      return (
        <div className="admin-prices">
          <div className="price-icons-container-horizontal">
            <Tooltip title="Precio final que verá el cliente">
              <div className="price-icon-with-label">
                <UserOutlined className="price-icon customer-icon" />
                <span className="price-value customer-price-value">
                  {formatPrice(
                    product.newPrice && product.newPrice !== product.price
                      ? product.newPrice
                      : product.price
                  )}
                </span>
              </div>
            </Tooltip>

            {product.newPrice && product.newPrice !== product.price && (
              <Tooltip title="Precio original del proveedor">
                <div className="price-icon-with-label">
                  <ShopOutlined className="price-icon supplier-icon" />
                  <span className="price-value supplier-price-value">
                    $
                    {typeof product.price === "string"
                      ? parseFloat(product.price).toFixed(2)
                      : product.price.toFixed(2)}
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="customer-price-simple">
        {formatPrice(
          product.newPrice && product.newPrice !== product.price
            ? product.newPrice
            : product.price
        )}
      </div>
    );
  };

  

  const renderTermsModal = () => (
    <Modal
      open={isTermsModalOpen}
      onCancel={handleCloseTerms}
      footer={null}
      centered
      width={800}
      className="terms-modal"
    >
      <div style={{ padding: "24px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "8px", color: "#0F5197", textAlign: "center" }}>
          Términos y Condiciones
        </h2>
        <p style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#0F5197", textAlign: "center" }}>
          EPNO Corporation
        </p>
        <p style={{ lineHeight: "1.6", color: "#555", marginBottom: "20px", textAlign: "justify" }}>
          Bienvenido a epno.com.mx. Al acceder o utilizar nuestra plataforma,
          usted acepta cumplir y estar sujeto a los siguientes Términos y
          Condiciones. Si no está de acuerdo con ellos, le recomendamos no
          utilizar el sitio.
        </p>
        <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "10px" }}>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              1. Uso del Sitio Web
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              epno.com.mx es una plataforma de órdenes en línea y Marketplace
              diseñada para facilitar la compra de productos y contratación de
              servicios industriales. Al usar este sitio web, usted se
              compromete a utilizarlo únicamente para fines legales y de acuerdo
              con estos términos.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              2. Propiedad Intelectual
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              Todo el contenido, diseños, logotipos, marcas registradas, textos,
              imágenes y software disponibles en epno.com.mx son propiedad
              exclusiva de EPNO Corporation o de sus licenciantes, y están
              protegidos por las leyes de propiedad intelectual aplicables.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Queda prohibida su reproducción, distribución, modificación o uso
              sin autorización previa y por escrito.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              3. Marketplace y Proveedores Externos
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              epno.com.mx actúa únicamente como una plataforma tecnológica que
              conecta compradores con proveedores externos.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              EPNO Corporation no fabrica, vende ni entrega directamente
              productos o servicios. Todas las transacciones, condiciones
              comerciales y responsabilidades corresponden exclusivamente a los
              proveedores y compradores involucrados.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              4. Limitación de Responsabilidad
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              EPNO Corporation no será responsable por daños, pérdidas o
              perjuicios derivados de transacciones realizadas con proveedores
              externos, incluyendo, sin limitarse a, calidad de productos,
              incumplimientos, retrasos en entrega o servicios postventa.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Cualquier controversia deberá resolverse directamente entre el
              comprador y el proveedor.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              5. Pagos y Reembolsos
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              Los pagos se procesan conforme a las políticas establecidas por
              cada proveedor y a través de la pasarela de pago Openpay.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              EPNO Corporation no es responsable por disputas de pago,
              contracargos o procesos de reembolso derivados de dichas
              transacciones.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              6. Política de Privacidad
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              El tratamiento de los datos personales se realiza conforme a
              nuestro Aviso de Privacidad, disponible en el sitio web.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Al utilizar la plataforma, el usuario consiente la recopilación,
              uso y protección de sus datos conforme a dicho aviso.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              7. Modificaciones a los Términos
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              EPNO Corporation se reserva el derecho de modificar estos Términos
              y Condiciones en cualquier momento.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Las modificaciones serán publicadas en esta misma sección y
              entrarán en vigor a partir de su publicación.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              8. Ley Aplicable y Jurisdicción
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              Estos términos se rigen por las leyes vigentes de los Estados
              Unidos Mexicanos.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Cualquier controversia derivada del uso de epno.com.mx se someterá
              a la jurisdicción de los tribunales competentes en México.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              9. Contacto
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              Para cualquier duda, aclaración o comentario relacionado con estos
              Términos y Condiciones, puede contactarnos en:
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "8px", fontWeight: "600" }}>
              EPNO Corporation
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "4px" }}>
            📍 <strong>Dirección:</strong> Municipio Libre 3529-D, Tres
            Hermanos Areco, Cd. Juárez, Chihuahua, México. C.P. 32385
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "4px" }}>
            📧 <strong>Correo electrónico:</strong> soporte@epno.com.mx
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "4px" }}>
            📞 <strong>Teléfono:</strong> 656 831 5340
            </p>
          </section>
        </div>
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Button type="primary" onClick={handleCloseTerms} style={{ background: "#0F5197" }}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderPrivacyModal = () => (
    <Modal
      open={isPrivacyModalOpen}
      onCancel={handleClosePrivacy}
      footer={null}
      centered
      width={800}
      className="privacy-modal"
    >
      <div style={{ padding: "24px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "8px", color: "#0F5197", textAlign: "center" }}>
          Aviso de Privacidad
        </h2>
        <p style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#0F5197", textAlign: "center" }}>
          EPNO Corporation
        </p>
        <p style={{ lineHeight: "1.6", color: "#555", marginBottom: "20px", textAlign: "justify" }}>
          En cumplimiento con lo dispuesto por la Ley Federal de Protección de
          Datos Personales en Posesión de los Particulares y su Reglamento, EPNO
          Corporation, a través del sitio web epno.com.mx, pone a su disposición
          el presente Aviso de Privacidad, con el fin de informarle sobre el
          tratamiento que se dará a sus datos personales.
        </p>
        <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "10px" }}>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              1. Responsable del Tratamiento de los Datos Personales
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
              EPNO Corporation es responsable del uso y protección de sus datos
              personales.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                fontWeight: "600",
              }}
            >
              Domicilio:
            </p>
            <p style={{ lineHeight: "1.6", color: "#555" }}>
              Municipio Libre 3529-D, Tres Hermanos Areco, Cd. Juárez,
              Chihuahua, México. C.P. 32385
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "4px" }}>
              Correo electrónico: soporte@epno.com.mx
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "4px" }}>
              Teléfono: 656 831 5340
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              2. Datos Personales que Recabamos
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              Los datos personales que podemos recabar, de manera directa o a
              través de nuestra plataforma, incluyen:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>Nombre completo</li>
              <li>Correo electrónico</li>
              <li>Teléfono de contacto</li>
              <li>Dirección fiscal y/o de envío</li>
              <li>Información de facturación</li>
              <li>
                Información relacionada con órdenes, cotizaciones y
                transacciones
              </li>
              <li>Datos de acceso y uso de la plataforma</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              EPNO Corporation no recaba datos personales sensibles, conforme a
              lo definido por la legislación aplicable.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              3. Finalidades del Tratamiento de Datos
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", fontWeight: "600" }}>
              Finalidades primarias:
            </p>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              Sus datos personales serán utilizados para:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>Registrar y administrar su cuenta en la plataforma</li>
              <li>Procesar órdenes, cotizaciones y transacciones</li>
              <li>
                Facilitar la comunicación entre compradores y proveedores
                externos
              </li>
              <li>Gestionar pagos a través de pasarelas autorizadas</li>
              <li>Dar cumplimiento a obligaciones legales y contractuales</li>
              <li>Brindar soporte y atención al cliente</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                fontWeight: "600",
                marginTop: "12px",
              }}
            >
              Finalidades secundarias:
            </p>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              Adicionalmente, sus datos podrán ser utilizados para:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>
                Envío de comunicaciones informativas, operativas o comerciales
              </li>
              <li>
                Mejora continua de la plataforma, servicios y experiencia del
                usuario
              </li>
              <li>Análisis estadístico y de uso del sitio</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Si no desea que sus datos sean tratados para estas finalidades
              secundarias, puede manifestarlo enviando un correo a
              soporte@epno.com.mx.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              4. Transferencia de Datos Personales
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              EPNO Corporation no vende ni renta sus datos personales.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              No obstante, sus datos podrán ser compartidos únicamente en los
              siguientes casos:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>
                Con proveedores externos involucrados en la atención de órdenes
                o servicios
              </li>
              <li>
                Con proveedores de servicios tecnológicos o pasarelas de pago
                necesarios para la operación del sitio
              </li>
              <li>Cuando sea requerido por autoridad competente</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Dichas transferencias se realizan bajo estrictos estándares de
              confidencialidad y seguridad, y conforme a la ley aplicable.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
            5. Derechos ARCO (Acceso, Rectificación, Cancelación y Oposición)
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              Usted tiene derecho a:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>Acceder a sus datos personales</li>
              <li>Rectificarlos si son inexactos o incompletos</li>
              <li>
                Cancelarlos cuando considere que no se requieren para las
                finalidades señaladas
              </li>
              <li>Oponerse al tratamiento de los mismos</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Para ejercer sus derechos ARCO, deberá enviar una solicitud al
              correo soporte@epno.com.mx, indicando:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>Nombre del titular</li>
              <li>Derecho que desea ejercer</li>
              <li>Descripción clara de los datos involucrados</li>
              <li>Medio para recibir respuesta</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              La respuesta se dará conforme a los plazos establecidos por la
              ley.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              6. Revocación del Consentimiento
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              Usted puede revocar en cualquier momento el consentimiento
              otorgado para el tratamiento de sus datos personales, enviando su
              solicitud al correo indicado.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              La revocación no tendrá efectos retroactivos.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
            7. Uso de Cookies y Tecnologías de Rastreo
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              El sitio epno.com.mx utiliza cookies y tecnologías similares para:
            </p>
            <ul
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                marginLeft: "20px",
              }}
            >
              <li>Mejorar la experiencia de navegación</li>
              <li>Analizar el tráfico y uso del sitio</li>
            </ul>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Puede deshabilitar el uso de cookies desde la configuración de su
              navegador; sin embargo, esto podría limitar algunas
              funcionalidades del sitio.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              8. Medidas de Seguridad
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              EPNO Corporation implementa medidas de seguridad administrativas,
              técnicas y físicas razonables y acordes con los estándares de la
              industria para proteger sus datos personales contra daño, pérdida,
              alteración, uso o acceso no autorizado.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              9. Cambios al Aviso de Privacidad
            </h3>
            <p
              style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}
            >
              EPNO Corporation se reserva el derecho de modificar o actualizar
              este Aviso de Privacidad en cualquier momento.
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#555",
                marginTop: "8px",
                textAlign: "justify",
              }}
            >
              Las modificaciones estarán disponibles en epno.com.mx e indicarán
              la fecha de última actualización.
            </p>
          </section>
          <section style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              10. Aceptación
            </h3>
            <p style={{ lineHeight: "1.6", color: "#555", textAlign: "justify" }}>
            Al utilizar el sitio web epno.com.mx, usted reconoce haber leído y
              entendido el presente Aviso de Privacidad y otorga su
              consentimiento para el tratamiento de sus datos personales
              conforme a lo aquí descrito.
            </p>
            <p style={{ lineHeight: "1.6", color: "#555", marginTop: "12px", fontWeight: "600" }}>
              Última Actualización: 12-01-2026
            </p>
          </section>
        </div>
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Button type="primary" onClick={handleClosePrivacy} style={{ background: "#0F5197" }}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );

  const showTopMarketing =
    promoBanners.length > 0 || featuredBanners.length > 0;
  const hasActiveSearch = searchTerm.trim() !== "";
  const quickActionButtons = [
    {
      key: "offers",
      label: "Ofertas",
      icon: <TagOutlined />,
      className: "quick-action--offers",
    },
    {
      key: "top",
      label: "Productos Top",
      icon: <RiseOutlined />,
      className: "quick-action--top",
    },
    {
      key: "special",
      label: "Productos en Especial",
      icon: <StarOutlined />,
      className: "quick-action--special",
    },
  ];

  return (
    <LayoutPage>
      {renderLoginModal()}
      {renderOffersModal()}
      {renderTopModal()}
      {renderSpecialModal()}
      {renderQuoteRequestModal()}
      {renderTermsModal()}
      {renderPrivacyModal()}
      <div className="marketplace-full-layout">
        <div className="marketplace-main-content">
          {!hasActiveSearch && (
            <>
              <div className="main-banner-extended">{renderMainBanner()}</div>

              <div className="marketplace-about-modal">
                <div className="about-modal-content">
                  <div className="about-modal-text">
                    <h2>Tu aliado en proveeduría industrial</h2>
                    <p>
                      Descubre como podemos optimizar tu cadena de suministro
                      con soluciones industriales de calidad. Conoce nuestra
                      experiencia, productos y servicios especializados.
                    </p>
                    <Button
                      type="primary"
                      size="large"
                      href="https://conocenos.epno.com.mx/"
                      target="_blank"
                      rel="noreferrer"
                      className="about-modal-cta"
                    >
                      Conoce nuestra empresa
                      <span className="about-modal-cta-icon">→</span>
                    </Button>
                  </div>
                  <div
                    className="about-modal-media"
                    style={{ backgroundImage: "url(/img/lg-bg.jpg)" }}
                    role="img"
                    aria-label="EPNO planta industrial"
                  />
                </div>
              </div>

              <div className="marketplace-categories-section">
                <div className="categories-header">
                  <h3>Conoce nuestras categorías</h3>
                </div>
                <div className="categories-carousel" ref={categoriesScrollRef}>
                  {carouselCategories.map((categoryItem) => (
                    <div
                      key={categoryItem.slug}
                      className="category-card"
                      style={{
                        backgroundImage: `url(${getCategoryCardImage(
                          categoryItem
                        )})`,
                      }}
                      onClick={() =>
                        history(
                          `/marketplace/products/filter?category=${categoryItem.slug}`
                        )
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          history(
                            `/marketplace/products/filter?category=${categoryItem.slug}`
                          );
                        }
                      }}
                    >
                      <div className="category-overlay">
                        <span>{categoryItem.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {showTopMarketing && (
                <div className="marketing-top-section">
                  <div className="marketing-inline-wrapper">
                    {renderInlineBannerSection(
                      "Promociones",
                      "Sección de ofertas especiales",
                      promoBanners,
                      "promos"
                    )}
                    {renderInlineBannerSection(
                      "Destacados",
                      "Productos destacados",
                      featuredBanners,
                      "featured"
                    )}
                  </div>
                </div>
              )}

              <div className="marketplace-quick-actions">
                <h3 className="quick-actions-title">
                  Tenemos las mejores opciones para tu industria
                </h3>
                <div className="quick-actions-grid">
                  {quickActionButtons.map((button) => (
                    <button
                      key={button.key}
                      className={`quick-action-button ${button.className}`}
                      type="button"
                      aria-label={button.label}
                      onClick={() => {
                        if (button.key === "offers") {
                          handleOpenOffers();
                        }
                        if (button.key === "top") {
                          handleOpenTop();
                        }
                        if (button.key === "special") {
                          handleOpenSpecial();
                        }
                      }}
                    >
                      <span className="quick-action-icon">{button.icon}</span>
                      <span className="quick-action-label">{button.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="marketplace-quote-panel">
                <div className="quote-panel-icon" aria-hidden="true">
                  <SearchOutlined />
                </div>
                <div className="quote-panel-content">
                  <h4>¿No encuentras lo que buscas?</h4>
                  <p>
                    Cotiza cualquier producto o servicio que necesites y te
                    ayudaremos a encontrarlo
                  </p>
                </div>
                <div className="quote-panel-actions">
                  <Button
                    type="default"
                    size="large"
                    className="quote-panel-button"
                    icon={<MessageOutlined />}
                    onClick={handleOpenQuoteRequest}
                  >
                    Solicitar Cotización
                  </Button>
                  <span>
                    Respuesta rápida • Sin compromiso • Precios competitivos
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="products-content-section">
            {loading ? (
              <div className="loading-container">
                <Spin size="large" />
                <p>Cargando marketplace...</p>
              </div>
            ) : (
              <>
                {(category || searchTerm) && (
                  <div className="current-filter-header">
                    <Alert
                      message={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            {searchTerm
                              ? `Resultados de búsqueda para: "${searchTerm}"`
                              : `Categoría: ${currentCategory}`}
                            <Tag color="blue" style={{ marginLeft: "8px" }}>
                              {filteredProducts.length} productos
                            </Tag>
                          </span>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button size="small" onClick={showAllFilteredProducts}>
                              Ver todos los productos
                            </Button>
                            <Button size="small" type="default" onClick={clearFilters}>
                              Volver al inicio
                            </Button>
                          </div>
                        </div>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: "16px" }}
                    />
                  </div>
                )}

                <div className="products-section">
                  {error && (
                    <Alert
                      message="Error"
                      description={error}
                      type="error"
                      showIcon
                      style={{ marginBottom: "16px" }}
                    />
                  )}

                  <h2 className="products-section-title">
                    Los mejores productos para tu negocio
                  </h2>

                  <div className="products-container">
                    {filteredProducts.length === 0 ? (
                      <Empty
                        description={
                          searchTerm
                            ? `No se encontraron productos para "${searchTerm}"`
                            : category
                            ? `No hay productos en la categoría ${currentCategory}`
                            : "No hay productos disponibles"
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ) : (
                      <>
                        {(() => {
                          const pageProducts = getCurrentPageProducts();
                          const firstProducts = pageProducts.slice(0, 6);
                          const remainingProducts = pageProducts.slice(6);

                          return (
                            <>
                              <Row gutter={[12, 12]} className="products-grid">
                                {firstProducts.map(renderProductCard)}
                              </Row>

                              {remainingProducts.length > 0 && (
                                <Row
                                  gutter={[12, 12]}
                                  className="products-grid"
                                >
                                  {remainingProducts.map((product, index) =>
                                    renderProductCard(product, index + 6)
                                  )}
                                </Row>
                              )}
                            </>
                          );
                        })()}

                        {hasMoreProducts && (
                          <div className="load-more-section">
                            <Button
                              type="link"
                              size="large"
                              onClick={loadMoreProducts}
                              className="load-more-button"
                            >
                              Ver más productos
                            </Button>
                          </div>
                        )}

                        <div className="marketplace-cta-section">
                          <h2 className="cta-title">
                            Calidad a solo un clic de distancia
                          </h2>
                          <Button
                            type="primary"
                            size="large"
                            className="cta-register-button"
                            onClick={() => history("/register")}
                          >
                            Registrarse
                          </Button>
                          <p className="cta-login-text">
                            ¿Ya eres cliente?{" "}
                            <a href="/#/login" className="cta-login-link">
                              Iniciar sesión
                            </a>
                          </p>
                          <Button
                            type="primary"
                            size="large"
                            className="cta-scroll-top-button"
                            onClick={() =>
                              window.scrollTo({ top: 0, behavior: "smooth" })
                            }
                          >
                            Volver al inicio de la página
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="marketplace-footer">
            <div className="footer-content">
              <div className="footer-column footer-column--brand">
                <img src={FooterLogo} alt="EP&O" className="footer-brand" />
                <p className="footer-tagline">
                  La era digital de la proveeduría industrial.
                </p>
                <div className="footer-social-row">
                  <a
                    href="https://www.facebook.com/epnomx/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-button"
                    aria-label="Facebook"
                  >
                    <img
                      src={FacebookWhiteLogo}
                      alt="Facebook"
                      className="footer-social-icon"
                    />
                  </a>
                  <a
                    href="https://www.instagram.com/epnomx/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-button"
                    aria-label="Instagram"
                  >
                    <img
                      src={InstagramWhiteLogo}
                      alt="Instagram"
                      className="footer-social-icon"
                    />
                  </a>
                  <a
                    href="https://www.linkedin.com/authwall?trk=bf&trkInfo=AQEks47JezzT3wAAAZyQp9C4ngjwa9J5c-llY5ePoSlAA5ilfd06QP2or7dQTMOYt-8pFcuRutTYdUuXdHJA2ffrlJc-lNwWi20brOlYIQIppVMzNyzoGe4cxe5ggSWvNzr1yDY=&original_referer=&sessionRedirect=https%3A%2F%2Fwww.linkedin.com%2Fcompany%2Fepno%2F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-button"
                    aria-label="LinkedIn"
                  >
                    <img
                      src={LinkedinWhiteLogo}
                      alt="LinkedIn"
                      className="footer-social-icon"
                    />
                  </a>
                  <a
                    href="https://www.tiktok.com/@epnomx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-button"
                    aria-label="TikTok"
                  >
                    <img
                      src={TikTokWhiteLogo}
                      alt="TikTok"
                      className="footer-social-icon"
                    />
                  </a>
                  <a
                    href="https://www.youtube.com/@epnomx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-button"
                    aria-label="YouTube"
                  >
                    <img
                      src={YoutubeWhiteLogo}
                      alt="YouTube"
                      className="footer-social-icon"
                    />
                  </a>
                </div>
              </div>

              <div className="footer-column">
                <h4 className="footer-title">Conócenos</h4>
                <ul className="footer-links">
                  <li>
                    <a
                      href="https://conocenos.epno.com.mx/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Sobre Nosotros
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://conocenos.epno.com.mx/proveeduria"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Trabaja con nosotros
                    </a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-title">OTROS SITIOS</h4>
                <ul className="footer-links">
                  <li>
                    <a
                      href="https://www.mexus-staffing.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      MexUs
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://club.epno.com.mx/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Partners Club
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.spendgaroo.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Spendgaroo
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.youtube.com/@Vocesdelaindustria"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Voces de la industria
                    </a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-title">Soporte Técnico</h4>
                <ul className="footer-links">
                  <li>
                    <a href="mailto:soporte@epno.com.mx">soporte@epno.com.mx</a>
                  </li>
                  <li>
                    <a
                      className="faq-link"
                      href="https://conocenos.epno.com.mx/#:~:text=TIENES%20DUDAS%3F%20AQUI%20LAS%20RESOLVEMOS,TODO%20LO%20QUE%20NECESITAS%20SABER%20PARA%20EMPEZAR%20A%20USAR%20EP%26O%20CON%20CONFIANZA."
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Preguntas Frecuentes
                    </a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-title">Métodos de Pago</h4>
                <div className="payment-methods">
                  <div className="payment-logos">
                    <img
                      src="/img/pagos/visa.png"
                      alt="Visa"
                      className="payment-logo"
                    />
                    <img
                      src="/img/pagos/masterCard.png"
                      alt="MasterCard"
                      className="payment-logo"
                    />
                    <img
                      src="/img/pagos/americanExpress.png"
                      alt="American Express"
                      className="payment-logo"
                    />
                    <img
                      src="/img/pagos/carnet.png"
                      alt="Carnet"
                      className="payment-logo"
                    />
                    <img
                      src="/img/pagos/LogotipoOpenpay-02.jpg"
                      alt="OpenPay"
                      className="payment-logo"
                    />
                  </div>
                  <div className="contact-info">
                    {/* <p>
                      <span className="contact-icon">📞</span> +52 (656)
                      831-5340
                    </p> */}
                    <p>
                      <span className="contact-icon">📍</span> Cd. Juárez, Chih.
                      México
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-bottom">
              <div className="footer-bottom-links">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenTerms();
                  }}
                >
                  Términos y Condiciones
                </a>
                <span className="footer-separator">•</span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenPrivacy();
                  }}
                >
                  Aviso de Privacidad
                </a>
              </div>
              <p className="footer-copyright">
                © 2026 EP&O Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </LayoutPage>
  );
}